import {
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  AfterViewInit
} from '@angular/core';
import { Subject } from 'rxjs';
import { ModalConfig, ModalResult } from '../../../shared/models/design-system.types';

/**
 * Modal Component
 *
 * Internal component used by ModalService.
 * Not meant to be used directly in templates.
 */
@Component({
    selector: 'hc-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    standalone: false
})
export class ModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentContainer', { read: ViewContainerRef, static: true })
  contentContainer!: ViewContainerRef;

  @ViewChild('modalDialog', { static: true })
  modalDialog!: ElementRef<HTMLElement>;

  config: ModalConfig = {};
  afterClosed?: Subject<ModalResult>;
  closeHandler?: (result: ModalResult) => void;
  contentComponent?: ComponentRef<unknown>;

  // Confirm dialog properties
  isConfirmDialog = false;
  confirmMessage = '';
  confirmText = 'Confirm';
  cancelText = 'Cancel';
  confirmVariant: 'primary' | 'danger' = 'primary';

  isVisible = false;

  // Focus management
  private previouslyFocusedElement: HTMLElement | null = null;
  private readonly FOCUSABLE_SELECTORS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    // Save currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Insert content component if provided
    if (this.contentComponent) {
      this.contentContainer.insert(this.contentComponent.hostView);
    }

    // Trigger animation
    setTimeout(() => {
      this.isVisible = true;
    }, 10);
  }

  ngAfterViewInit(): void {
    // Focus the modal after animation starts
    setTimeout(() => {
      this.focusFirstElement();
    }, 50);
  }

  ngOnDestroy(): void {
    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      setTimeout(() => {
        this.previouslyFocusedElement?.focus();
      }, 0);
    }
  }

  /** Focus the first focusable element inside the modal */
  private focusFirstElement(): void {
    const modalElement = this.elementRef.nativeElement as HTMLElement;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(this.FOCUSABLE_SELECTORS);

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    } else {
      // If no focusable elements, focus the modal dialog itself
      this.modalDialog?.nativeElement?.focus();
    }
  }

  /** Trap focus inside the modal */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const modalElement = this.elementRef.nativeElement as HTMLElement;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(this.FOCUSABLE_SELECTORS);

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift+Tab on first element -> go to last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    }
    // Tab on last element -> go to first
    else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.config.keyboard !== false && this.config.closable !== false) {
      this.close();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (
      event.target === event.currentTarget &&
      this.config.backdrop !== 'static' &&
      this.config.backdrop !== false
    ) {
      this.close();
    }
  }

  close(confirmed = false): void {
    this.isVisible = false;

    // Wait for animation to complete
    setTimeout(() => {
      if (this.closeHandler) {
        this.closeHandler({ confirmed });
      }
    }, 200);
  }

  onConfirm(): void {
    this.close(true);
  }

  onCancel(): void {
    this.close(false);
  }

  get sizeClass(): string {
    const size = this.config.size ?? 'md';
    return `hc-modal--${size}`;
  }

  get centeredClass(): string {
    return this.config.centered ? 'hc-modal--centered' : '';
  }
}
